import React, { useState } from 'react';
import { CreditCard } from 'lucide-react';
import { CurrentSubscription, SubscriptionPlan, trialDaysLeft } from '../../../types/subscription.types';

interface TrialStatusCardProps {
  subscription: CurrentSubscription;
  currentPlan: SubscriptionPlan | undefined;
  starterPlan: SubscriptionPlan | undefined;
  onSwitchToStarter: () => void;
  onScrollToPlans: () => void;
  onManageBilling: () => void;
  onCancelConfirmed: () => Promise<void>;
}

const formatDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—';

const TrialStatusCard: React.FC<TrialStatusCardProps> = ({
  subscription,
  currentPlan,
  starterPlan,
  onSwitchToStarter,
  onScrollToPlans,
  onManageBilling,
  onCancelConfirmed,
}) => {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [canceling, setCanceling] = useState(false);

  const daysLeft = trialDaysLeft(subscription.current_period_end);
  const trialEndLabel = formatDate(subscription.current_period_end);
  const trialStartLabel = formatDate(subscription.current_period_start);
  const reminderDate = subscription.current_period_end
    ? (() => {
        const d = new Date(subscription.current_period_end);
        d.setDate(d.getDate() - 3);
        return d;
      })()
    : null;

  const showSaveOffer = ['growth', 'scale'].includes(subscription.plan_name) && !!starterPlan;

  const handleConfirmCancel = async () => {
    setCanceling(true);
    try {
      await onCancelConfirmed();
      setShowCancelModal(false);
    } finally {
      setCanceling(false);
    }
  };

  const timelineItems = [
    {
      label: currentPlan?.trial_fee_display
        ? `Trial started · ${currentPlan.trial_fee_display} paid`
        : 'Trial started',
      date: trialStartLabel,
      done: true,
    },
    {
      label: daysLeft === 0 ? 'Trial ends today' : `Today · ${daysLeft ?? 0} ${daysLeft === 1 ? 'day' : 'days'} left`,
      date: 'Full access',
      done: false,
      current: true,
    },
    { label: 'Reminder email', date: reminderDate ? formatDate(reminderDate.toISOString()) : '—', done: false },
    { label: `First charge · ${currentPlan?.price_display ?? ''}`, date: trialEndLabel, done: false, last: true },
  ];

  return (
    <div className="mb-6 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm sm:mb-8">
      <div className="border-b border-gray-100 px-5 py-5 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-extrabold text-secondary">{subscription.plan_display_name}</h2>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-accent/30 bg-accent/10 px-2.5 py-1 text-xs font-bold text-[#245C74]">
              <span className="h-1.5 w-1.5 rounded-full bg-accent" />
              Trial · {daysLeft === 0 ? 'ends today' : `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left`}
            </span>
          </div>
          {currentPlan && (
            <div className="text-sm text-gray-500">
              {currentPlan.price_display}
              <span className="text-xs">/mo</span>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2.5 px-5 py-4 text-sm sm:px-6">
        <p className="text-secondary">
          Your {subscription.plan_display_name} trial ends <b className="font-bold">{trialEndLabel}</b>. We&apos;ll
          charge <b className="font-bold">{currentPlan?.price_display ?? ''}</b>, the full price of your first
          month, on {trialEndLabel}
          {currentPlan?.trial_fee_display && (
            <> — on top of the {currentPlan.trial_fee_display} trial fee you already paid, not credited</>
          )}
          .
        </p>
      </div>

      <div className="px-5 pb-5 sm:px-6">
        <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 pb-1 pt-4">
          <div className="mb-4 text-[11px] font-bold uppercase tracking-wide text-gray-400">Trial timeline</div>
          {timelineItems.map((item) => (
            <div key={item.label} className="relative flex gap-3 pb-4">
              <div className="flex flex-none flex-col items-center">
                <span
                  className={`h-3.5 w-3.5 rounded-full border-[2.5px] ${
                    item.done
                      ? 'border-map-green bg-map-green'
                      : item.current
                      ? 'border-primary bg-white'
                      : 'border-gray-300 bg-white'
                  }`}
                />
                {!item.last && <span className={`mt-0.5 w-0.5 flex-1 ${item.done ? 'bg-map-green/40' : 'bg-gray-200'}`} />}
              </div>
              <div className="-mt-0.5 pb-0.5">
                <div className={`text-[13.5px] ${item.current ? 'font-bold text-primary' : 'font-semibold text-secondary'}`}>
                  {item.label}
                </div>
                <div className="mt-0.5 text-xs text-gray-400">{item.date}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 px-5 pb-5 sm:px-6">
        <button
          onClick={onScrollToPlans}
          className="min-w-[140px] flex-1 rounded-xl border-[1.5px] border-gray-200 bg-white py-3 text-sm font-semibold text-secondary hover:border-gray-300"
        >
          Switch plan
        </button>
        <button
          onClick={() => setShowCancelModal(true)}
          className="min-w-[140px] flex-1 rounded-xl border-[1.5px] border-[#E7C9CA] bg-white py-3 text-sm font-semibold text-primary hover:bg-primary/5"
        >
          Cancel trial
        </button>
      </div>

      <div className="border-t border-gray-100 px-5 py-4 sm:px-6">
        <button onClick={onManageBilling} className="flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-primary">
          <CreditCard className="h-4 w-4" />
          Manage billing
        </button>
      </div>

      {showSaveOffer && starterPlan && (
        <div className="mx-5 mb-5 rounded-xl border border-map-green/30 bg-map-green/10 p-4 sm:mx-6">
          <p className="text-[13.5px] leading-relaxed text-[#2F5C48]">
            <b className="font-bold">Not ready for {currentPlan?.price_display}/mo?</b> Switch to Starter and pay{' '}
            <b className="font-bold">{starterPlan.price_display}/mo</b> instead. You keep your trial dates and your
            saved results.
            {currentPlan?.trial_fee_display &&
              starterPlan.trial_fee_display &&
              currentPlan.trial_fee_display !== starterPlan.trial_fee_display && (
                <>
                  {' '}
                  The difference between the trial fees isn&apos;t refunded automatically, but you can request it.
                </>
              )}
          </p>
          <button
            onClick={onSwitchToStarter}
            className="mt-3 w-full rounded-xl bg-map-green py-2.5 text-[13.5px] font-bold text-white hover:bg-map-green-hover"
          >
            Switch to Starter
          </button>
        </div>
      )}

      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-secondary/40 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
            <div className="px-7 pt-7">
              <h3 className="text-xl font-extrabold text-secondary">Cancel your trial?</h3>
              <p className="mt-2.5 text-[14.5px] leading-relaxed text-gray-600">
                You keep full access until <b className="font-bold text-secondary">{trialEndLabel}</b>. There&apos;s
                no further charge.
                {currentPlan?.trial_fee_display && (
                  <>
                    {' '}
                    The {currentPlan.trial_fee_display} trial fee you already paid isn&apos;t refunded
                    automatically, but you can request a refund anytime, no questions asked.
                  </>
                )}
              </p>
            </div>
            <div className="mx-7 mt-5 flex gap-2.5 rounded-xl border border-gray-100 bg-gray-50 p-4">
              <svg viewBox="0 0 24 24" fill="none" stroke="#4BA1C5" strokeWidth={2} className="mt-0.5 h-5 w-5 flex-none">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              <span className="text-[13px] leading-relaxed text-gray-500">
                Your results stay exportable for <b className="font-bold text-secondary">30 days</b> after
                cancellation. Nothing is deleted right away.
              </span>
            </div>
            <div className="flex flex-col gap-2.5 px-7 pb-7 pt-5">
              <button
                onClick={() => setShowCancelModal(false)}
                disabled={canceling}
                className="w-full rounded-xl bg-primary py-3.5 text-sm font-bold text-white hover:bg-primary-hover disabled:opacity-60"
              >
                Keep my trial
              </button>
              <button
                onClick={handleConfirmCancel}
                disabled={canceling}
                className="w-full rounded-xl border-[1.5px] border-gray-200 bg-white py-3 text-sm font-semibold text-gray-500 hover:bg-gray-50 disabled:opacity-60"
              >
                {canceling ? 'Canceling…' : 'Cancel trial anyway'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrialStatusCard;
